import { useMemo, useState } from "react";

import GoTo from "@/app/shared/GoTo";
import OverflowDrawer from "@/app/shared/OverflowDrawer";
import { selectableColors } from "@/design/colors/colors";
import Avatar from "@/design/components/Avatar";
import Editor from "@/design/components/Editor";
import EditorForm from "@/design/components/EditorForm";
import EditorList from "@/design/components/EditorList";
import EditorSidebar from "@/design/components/EditorSidebar";
import { SaveState } from "@/utils/hooks/useSave";
import Valkyrie, { viCircleDashed, viPlus } from "@sippy-platform/valkyrie";

import { Box, Button, ListItem, ListItemAvatar, ListItemButton, ListItemText } from "@mui/material";
import PropTypes from "prop-types";

import RuleButton from "./RuleButton";
import RuleForm from "./RuleForm";

function RuleEditor({ timetable, timetableDispatch, getGoTo, setSaveState }) {
  const [activeRule, setActiveRule] = useState(null);

  const availableColors = useMemo(
    () =>
      selectableColors.filter(
        (color) => !timetable.rules.find((rule) => rule.color === color[500]) || activeRule?.color === color[500],
      ),
    [activeRule?.color, timetable.rules],
  );

  const fallbackGoto = getGoTo(timetable.overflow);

  return (
    <Editor
      title="Destinations"
      actions={
        <Button
          variant="contained"
          startIcon={<Valkyrie icon={viPlus} />}
          onClick={() =>
            setActiveRule({
              overflow: null,
              color: availableColors[Math.floor(Math.random() * availableColors.length)][500],
            })
          }
        >
          Add
        </Button>
      }
    >
      <Box>
        <EditorList columns={2} direction="row">
          <ListItem disablePadding>
            <ListItemButton onClick={() => setActiveRule(null)} selected={activeRule === null}>
              <ListItemAvatar>
                <Avatar text="#fff" icon={fallbackGoto?.icon || viCircleDashed} color="#fff" />
              </ListItemAvatar>
              <ListItemText
                primary={<GoTo target={fallbackGoto} hideType />}
                secondary={
                  <>
                    {timetable.overflow && Object.keys(timetable.overflow).length > 0 && (
                      <>
                        <GoTo target={fallbackGoto} type /> &middot;{" "}
                      </>
                    )}
                    Fallback destination
                  </>
                }
              />
            </ListItemButton>
          </ListItem>
          {timetable.rules.map((rule) => (
            <RuleButton
              key={rule.id}
              timetable={timetable}
              rule={rule}
              activeRule={activeRule}
              setActiveRule={setActiveRule}
              timetableDispatch={timetableDispatch}
              getGoTo={getGoTo}
            />
          ))}
        </EditorList>
      </Box>
      <EditorSidebar showForm={true}>
        {activeRule ? (
          <RuleForm
            key={activeRule.id}
            timetable={timetable}
            rule={activeRule}
            timetableDispatch={timetableDispatch}
            setActiveRule={setActiveRule}
          />
        ) : (
          <EditorForm title="Fallback rule">
            <OverflowDrawer
              label="Destination"
              value={timetable.overflow}
              propertyName="overflow"
              overflowHandler={(value) => {
                timetableDispatch({ type: "overflow", value });
                setSaveState(SaveState.UNSAVED);
              }}
              filter={{ exten: [timetable.exten], custom: [], sound: [] }}
              helperText="Set a destination that should be redirected to when this rule is active."
            />
          </EditorForm>
        )}
      </EditorSidebar>
    </Editor>
  );
}

RuleEditor.propTypes = {
  timetable: PropTypes.object,
  timetableDispatch: PropTypes.func,
  getGoTo: PropTypes.func,
  setSaveState: PropTypes.func,
};

export default RuleEditor;
